import { TagTypes } from '@/types/api/tag-types'

import { baseApiSlice as api } from './base-api-slice'

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    listLists: build.query<ListListsApiResponse, ListListsApiArg>({
      query: () => ({ url: `/list/` }),
      providesTags: [TagTypes.FilterList],
    }),
    createList: build.mutation<CreateListApiResponse, CreateListApiArg>({
      query: (queryArg) => ({
        url: `/list/`,
        method: 'POST',
        body: queryArg.createFilterListRequest,
      }),
      invalidatesTags: [TagTypes.FilterList],
    }),
    createVersion: build.mutation<
      CreateVersionApiResponse,
      CreateVersionApiArg
    >({
      query: (queryArg) => ({
        url: `/list/${queryArg.listId}/`,
        method: 'POST',
        body: queryArg.updateFilterListRequest,
      }),
      invalidatesTags: [TagTypes.FilterList],
    }),
    deleteList: build.mutation<DeleteListApiResponse, DeleteListApiArg>({
      query: (queryArg) => ({
        url: `/list/${queryArg.listId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.FilterList],
    }),
    listVersions: build.query<ListVersionsApiResponse, ListVersionsApiArg>({
      query: (queryArg) => ({ url: `/list/${queryArg.listId}/version/` }),
      providesTags: [TagTypes.FilterList],
    }),
    getVersion: build.query<GetVersionApiResponse, GetVersionApiArg>({
      query: (queryArg) => ({
        url: `/list/${queryArg.listId}/version/${queryArg.versionId}/`,
      }),
    }),
  }),
  overrideExisting: false,
})
export { injectedRtkApi as filterListsApi }
export type ListListsApiResponse = /** status 200 Successful response */ {
  data?: FilterListShort[]
}
// biome-ignore lint/suspicious/noConfusingVoidType: Bulk suppression as part of biome migration
export type ListListsApiArg = void
export type CreateListApiResponse =
  /** status 200 Successful response */ FilterList
export type CreateListApiArg = {
  createFilterListRequest: CreateFilterListRequest
}
export type CreateVersionApiResponse =
  /** status 200 Successful response */ FilterList
export type CreateVersionApiArg = {
  /** The unique identifier of the filter list */
  listId: string
  updateFilterListRequest: UpdateFilterListRequest
}
export type DeleteListApiResponse = /** status 200 Successful response */ string
export type DeleteListApiArg = {
  /** The unique identifier of the filter list */
  listId: string
}
export type ListVersionsApiResponse = /** status 200 Successful response */ {
  data?: FilterListVersionShort[]
}
export type ListVersionsApiArg = {
  /** The unique identifier of the filter list */
  listId: string
}
export type GetVersionApiResponse =
  /** status 200 Successful response */ FilterList
export type GetVersionApiArg = {
  /** The unique identifier of the filter list */
  listId: string
  /** The version of the filter list */
  versionId: number
}
export type FilterListShort = {
  /** The unique identifier of the filter list */
  list_id: string
  /** The user defined name of the filter list */
  name: string
  /** The date and time the filter list was created */
  created_at: string
  /** The moment_user_id of the user who created the filter list */
  created_by: string
  /** The ref data field that the filter list is based on */
  field: string
  /** The version of the filter list */
  version: number
}
export type ValueDict = {
  value: string[]
  added_at: string[]
  added_by: string[]
}
export type FilterList = {
  /** The unique identifier of the filter list */
  list_id: string
  /** The user defined name of the filter list */
  name: string
  /** The organization ID of the filter list */
  organization_id: string
  /** The ref data field that the filter list is based on */
  field: string
  /** The date and time the filter list was updated */
  updated_at: string
  /** The moment_user_id of the user who updated the filter list */
  updated_by: string
  /** The version of the filter list */
  version: number
  /** Whether this version is the active version of the filter list */
  active: boolean
  values?: ValueDict
}
export type CreateFilterListRequest = {
  /** The user defined name of the filter list */
  name: string
  /** The ref data field that the filter list is based on */
  field: string
}
export type FilterListOperationAction = 'add' | 'remove' | 'replace' | 'rename_list'
export type FilterListOperationRequest = {
  action: FilterListOperationAction
  /** The values for the operation (required for add/remove/replace actions) */
  values: string[]
  /** The source file name for the operation */
  source?: string
}
export type UpdateFilterListRequest = {
  /** The operations to perform on the filter list */
  operations?: FilterListOperationRequest[]
}
export type FilterListOperationResponse = {
  action: FilterListOperationAction
  /** The number of values in the specificed operation action */
  num_values: number
  /** The number of values in the previous version of the filter list */
  prev_num_values?: number
  /** The source file name for the operation */
  source?: string
  /** The updated name of the filter list */
  filter_list_name?: string
}
export type FilterListVersionShort = {
  /** The version of the filter list */
  version: number
  /** The operations that were performed in this version */
  operations: FilterListOperationResponse[]
  /** The date and time the filter list was updated */
  updated_at?: string
  /** The moment_user_id of the user who updated the filter list */
  updated_by?: string
}
export const {
  useListListsQuery,
  useCreateListMutation,
  useCreateVersionMutation,
  useDeleteListMutation,
  useListVersionsQuery,
  useGetVersionQuery,
} = injectedRtkApi
